/**
 * /reset-password?token=… — новий пароль за посиланням з листа/Telegram
 * (ч.1073). Посилання одноразове, діє 30 хвилин; після зміни пароля —
 * вихід на всіх пристроях (і в цьому браузері теж).
 */
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { checkPasswordReset, confirmPasswordReset } from '@/api/auth';
import { clearSession } from '@/auth/store';
import { extractApiError } from '@/utils/extractApiError';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const check = useQuery({
    queryKey: ['password-reset-check', token],
    queryFn: () => checkPasswordReset(token!),
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
  });
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Пароль має містити мінімум 8 символів');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Паролі не співпадають');
      return;
    }
    setLoading(true);
    try {
      await confirmPasswordReset(token!, password);
      // Сесії користувача закрито на сервері — прибираємо й локальну.
      clearSession();
      setDone(true);
    } catch (err) {
      setError(extractApiError(err, 'Не вдалося змінити пароль'));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="login">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Пароль змінено</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Новий пароль збережено. З міркувань безпеки ми завершили всі
            сесії на інших пристроях — увійдіть з новим паролем.
          </p>
          <Link to="/login">
            <button type="button" className="primary" style={{ width: '100%' }}>увійти</button>
          </Link>
        </form>
      </div>
    );
  }

  if (!token || check.isError || check.data?.valid === false) {
    return (
      <div className="login">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Посилання недійсне</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Посилання застаріло (діє 30 хвилин), уже використане або замість
            нього надіслано нове. Запросіть посилання ще раз.
          </p>
          <Link to="/forgot-password">
            <button type="button" className="primary" style={{ width: '100%' }}>
              запросити нове посилання
            </button>
          </Link>
        </form>
      </div>
    );
  }

  if (check.isLoading) {
    return (
      <div className="login">
        <form onSubmit={(e) => e.preventDefault()}>Завантаження…</form>
      </div>
    );
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit}>
        <h1>Новий пароль</h1>
        <div className="field">
          <label>Новий пароль (мін. 8 символів)</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label>Повторіть пароль</label>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <button type="submit" className="primary" style={{ width: '100%' }} disabled={loading}>
          {loading ? 'збереження…' : 'зберегти пароль'}
        </button>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
